import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useCompetitorStore = create(
  persist(
    (set, get) => ({
      competitors: [
        {
          id: 'comp_001',
          name: 'TechVision Solutions',
          industry: 'Software Development',
          website: 'www.techvisionsolutions.com',
          headquarters: 'San Francisco, CA',
          foundedYear: 2015,
          employeeCount: 250,
          marketPosition: 'Direct Competitor',
          marketShare: 15.5,
          strengths: ['Strong brand recognition', 'Large customer base', 'Advanced AI features'],
          weaknesses: ['High pricing', 'Poor customer support', 'Legacy codebase'],
          opportunities: ['Emerging markets', 'New partnerships', 'Product expansion'],
          threats: ['New entrants', 'Technology disruption', 'Economic downturn'],
          keyProducts: ['CloudSync Pro', 'DataVault Enterprise', 'Analytics Suite'],
          pricingModel: 'Subscription-based ($50-500/month)',
          targetMarket: 'Enterprise, Mid-market',
          financialData: {
            annualRevenue: '$50M',
            fundingStatus: 'Series C - $25M',
            growthRate: '35% YoY',
          },
          contactInfo: {
            primaryContact: 'John Smith',
            title: 'VP Sales',
            email: 'john@techvisionsolutions.com',
            phone: '+1-555-0100',
          },
          socialMedia: {
            linkedin: 'https://linkedin.com/company/techvisionsolutions',
            twitter: '@techvision',
            facebook: 'https://facebook.com/techvisionsolutions',
          },
          lastUpdated: '2026-04-25',
          riskLevel: 'HIGH',
          notes: 'Main competitor in enterprise segment. Monitor closely.',
        },
        {
          id: 'comp_002',
          name: 'CloudWorks Inc',
          industry: 'Cloud Infrastructure',
          website: 'www.cloudworks.io',
          headquarters: 'Austin, TX',
          foundedYear: 2018,
          employeeCount: 120,
          marketPosition: 'Emerging Competitor',
          marketShare: 8.2,
          strengths: ['Innovative technology', 'Fast growth', 'Cost-effective solutions'],
          weaknesses: ['Limited market presence', 'Small team', 'New to market'],
          opportunities: ['International expansion', 'Enterprise partnerships', 'Product diversification'],
          threats: ['Acquisition by larger firms', 'Market saturation', 'Technology shifts'],
          keyProducts: ['CloudCore', 'DataStream', 'SecureCloud'],
          pricingModel: 'Pay-as-you-go ($0.01-0.50/unit)',
          targetMarket: 'SMB, Startups',
          financialData: {
            annualRevenue: '$5M',
            fundingStatus: 'Seed - $2M',
            growthRate: '120% YoY',
          },
          contactInfo: {
            primaryContact: 'Sarah Johnson',
            title: 'CEO',
            email: 'sarah@cloudworks.io',
            phone: '+1-555-0101',
          },
          socialMedia: {
            linkedin: 'https://linkedin.com/company/cloudworks',
            twitter: '@cloudworks_io',
            facebook: 'https://facebook.com/cloudworks',
          },
          lastUpdated: '2026-04-26',
          riskLevel: 'MEDIUM',
          notes: 'Fast growing startup. Could become major threat.',
        },
        {
          id: 'comp_003',
          name: 'EnterprisePro Ltd',
          industry: 'Enterprise Software',
          website: 'www.enterprisepro.com',
          headquarters: 'London, UK',
          foundedYear: 2010,
          employeeCount: 500,
          marketPosition: 'Indirect Competitor',
          marketShare: 12.0,
          strengths: ['Mature product', 'Global presence', 'Strong partnerships'],
          weaknesses: ['Outdated UI', 'Expensive', 'Slow innovation'],
          opportunities: ['Market consolidation', 'New verticals', 'AI integration'],
          threats: ['Market disruption', 'Talent loss', 'Regulatory changes'],
          keyProducts: ['EnterpriseSuite 2024', 'BusinessHub', 'ReportingEngine'],
          pricingModel: 'License-based ($1000-10000/year)',
          targetMarket: 'Large Enterprises',
          financialData: {
            annualRevenue: '$45M',
            fundingStatus: 'Publicly Traded',
            growthRate: '5% YoY',
          },
          contactInfo: {
            primaryContact: 'Michael Brown',
            title: 'Head of Sales',
            email: 'michael@enterprisepro.com',
            phone: '+44-207-555-0102',
          },
          socialMedia: {
            linkedin: 'https://linkedin.com/company/enterprisepro',
            twitter: '@enterprisepro',
            facebook: 'https://facebook.com/enterprisepro',
          },
          lastUpdated: '2026-04-20',
          riskLevel: 'MEDIUM',
          notes: 'Legacy player losing market share to modern solutions.',
        },
      ],

      // Competitor Actions
      addCompetitor: (competitor) =>
        set((state) => ({
          competitors: [...state.competitors, { ...competitor, id: `comp_${Date.now()}`, lastUpdated: new Date().toISOString().split('T')[0] }],
        })),

      updateCompetitor: (id, competitor) =>
        set((state) => ({
          competitors: state.competitors.map((c) =>
            c.id === id ? { ...c, ...competitor, lastUpdated: new Date().toISOString().split('T')[0] } : c
          ),
        })),

      deleteCompetitor: (id) =>
        set((state) => ({
          competitors: state.competitors.filter((c) => c.id !== id),
        })),

      getCompetitorById: (id) => get().competitors.find((c) => c.id === id),

      // Statistics
      getTotalCompetitors: () => get().competitors.length,
      getHighRiskCompetitors: () => get().competitors.filter((c) => c.riskLevel === 'HIGH').length,
      getAverageMarketShare: () => {
        const competitors = get().competitors
        return competitors.length > 0
          ? (competitors.reduce((sum, c) => sum + c.marketShare, 0) / competitors.length).toFixed(1)
          : 0
      },
    }),
    {
      name: 'competitor-store',
      getStorage: () => localStorage,
    }
  )
)
